// Trip-card hero photo resolver — ports mobile/lib/marquee-photos.ts +
// the heroQueryForDestId logic from mobile/lib/hero-photos.ts. Returns a POOL of
// photos (the card slideshows through them, like mobile's marquee), not one.
//
// Two stages, mirroring mobile:
//   1. Curated `display` photos — marquee.heroUrl, the pinned primary dest, all
//      heroSlides, extra urls. The catalog-backed refs are fetched per
//      destination from /api/trips/{id}/catalog/destinations/{destId} and
//      indexed by photoIdx (verified: japan's `fuji-lakes` → 8 photos).
//   2. /api/photos/hero — fanned out over the trip's STOPS (not just the first),
//      keyed by each stop's destId (resolved to a clean place name), collecting
//      a few real travel photos per stop.
//
// Query rules (keyed off destId, NEVER the human label — a label may be an event
// name like "SF Estonia Fest" that yields a garbage query):
//   - place:c<gnId>  → the bundle's bare city name (place:c5391959 → "San
//     Francisco") via the slim /places-names.json map.
//   - custom:<slug>  → the slug WITHOUT the `custom:` prefix, with the picker's
//     2-letter admin suffix expanded ("hood-river-or-usa" → "Hood River,
//     Oregon"; "dunsmuir-ca-usa" → "Dunsmuir, California"; "suttle-lake" →
//     "Suttle Lake"). Leaving the prefix on ("Custom:suttle Lake") fuzzy-matched
//     the wrong place — that's how Suttle Lake rendered Mt Hood; the abbreviated
//     suffix ("Hood River Or Usa") returned bland Openverse instead of Unsplash.
//   - bare catalog slug → Title-cased ("kyoto" → "Kyoto").
// Europeana results are dropped — that European historical archive returns odd
// B&W ethnography photos for obscure/non-European towns.

import { getHeroPhotos } from './api';
import { guestTokenForTrip } from './guest';
import { resolvePhoto } from './photo';
import type { TripListItem, TripStay } from './types';

export interface TripHero {
  url: string;
  credit?: string;
}

function cap(w: string): string {
  return w.charAt(0).toUpperCase() + w.slice(1);
}

// kebab slug → Title Case ("new-york-city" → "New York City").
function humanizeDestId(slug: string): string {
  return slug.split('-').filter(Boolean).map(cap).join(' ');
}

// US state/territory abbreviation → full name. The destination picker bakes the
// admin1 region into custom slugs as a 2-letter code + country ("hood-river-or-
// usa"). Expanding it to "Hood River, Oregon" turns a noisy query into one
// Unsplash actually matches.
const US_STATES: Record<string, string> = {
  al: 'Alabama', ak: 'Alaska', az: 'Arizona', ar: 'Arkansas', ca: 'California',
  co: 'Colorado', ct: 'Connecticut', de: 'Delaware', fl: 'Florida', ga: 'Georgia',
  hi: 'Hawaii', id: 'Idaho', il: 'Illinois', in: 'Indiana', ia: 'Iowa', ks: 'Kansas',
  ky: 'Kentucky', la: 'Louisiana', me: 'Maine', md: 'Maryland', ma: 'Massachusetts',
  mi: 'Michigan', mn: 'Minnesota', ms: 'Mississippi', mo: 'Missouri', mt: 'Montana',
  ne: 'Nebraska', nv: 'Nevada', nh: 'New Hampshire', nj: 'New Jersey', nm: 'New Mexico',
  ny: 'New York', nc: 'North Carolina', nd: 'North Dakota', oh: 'Ohio', ok: 'Oklahoma',
  or: 'Oregon', pa: 'Pennsylvania', ri: 'Rhode Island', sc: 'South Carolina',
  sd: 'South Dakota', tn: 'Tennessee', tx: 'Texas', ut: 'Utah', vt: 'Vermont',
  va: 'Virginia', wa: 'Washington', wv: 'West Virginia', wi: 'Wisconsin', wy: 'Wyoming',
  dc: 'District of Columbia',
};

// A custom: slug → a clean hero query. "<place>-<st>-usa" → "Place, State"
// (Hood River, Oregon); anything else → plain Title Case (Suttle Lake).
export function customSlugToQuery(slug: string): string {
  const parts = slug.split('-').filter(Boolean);
  if (parts.length >= 3 && parts[parts.length - 1] === 'usa') {
    const state = US_STATES[parts[parts.length - 2]];
    if (state) return `${parts.slice(0, -2).map(cap).join(' ')}, ${state}`;
  }
  return parts.map(cap).join(' ');
}

// Lazy, memoized fetch of the slim place-id → name map (served at
// /places-names.json, generated by scripts/build_places_names.js from the
// mobile bundle). Only loaded when a trip actually pins a place: id.
let placeNames: Promise<Record<string, string>> | null = null;
function loadPlaceNames(): Promise<Record<string, string>> {
  if (!placeNames) {
    placeNames = fetch('/places-names.json')
      .then((r) => {
        const ct = r.headers.get('content-type') || '';
        return r.ok && ct.includes('json') ? (r.json() as Promise<Record<string, string>>) : {};
      })
      .catch(() => ({}));
  }
  return placeNames;
}

export async function heroQueryForStay(stay: TripStay): Promise<string | null> {
  const destId = stay.destId;
  if (!destId) return stay.label?.trim() || null;
  if (destId.startsWith('place:')) {
    const bare = destId.slice('place:'.length);
    const names = await loadPlaceNames();
    return names[bare] || stay.label?.trim() || humanizeDestId(bare);
  }
  if (destId.startsWith('custom:')) return customSlugToQuery(destId.slice('custom:'.length));
  return humanizeDestId(destId);
}

interface DisplayCfg {
  marquee?: { heroUrl?: string } | null;
  heroPrimaryDest?: { destId: string; photoIdx: number } | null;
  heroSlides?: Array<{ destId: string; photoIdx: number }> | null;
  heroExtraUrls?: Array<string | { url?: string }> | null;
}

function bareUrl(e: string | { url?: string } | null | undefined): string | null {
  if (!e) return null;
  const u = typeof e === 'string' ? e : e.url;
  return u ? (resolvePhoto(u)?.src ?? u) : null;
}

function toHero(p: { url?: string; photographer?: string; source?: string }): TripHero | null {
  if (!p?.url) return null;
  return {
    url: resolvePhoto(p.url)?.src ?? p.url,
    credit: p.photographer ? `${p.photographer}${p.source ? ' · ' + p.source : ''}` : p.source,
  };
}

function dedupeHeros(list: TripHero[]): TripHero[] {
  const seen = new Set<string>();
  const out: TripHero[] = [];
  for (const h of list) {
    if (!h || seen.has(h.url)) continue;
    seen.add(h.url);
    out.push(h);
  }
  return out;
}

// Fetch one catalog destination's photo at photoIdx via the same trip-scoped
// route mobile's marquee uses. Returns null on a stub/miss (route falls through
// to the SPA shell → non-JSON) so the caller drops to the next candidate.
async function fetchDestPhoto(
  tripId: string,
  destId: string,
  photoIdx: number,
): Promise<string | null> {
  try {
    // Guest sessions read this trip-scoped catalog route via the grant
    // token — without it the guest homepage's hero photos all 401 and
    // every card falls back to the accent gradient.
    const guestToken = guestTokenForTrip(tripId);
    const res = await fetch(
      `/api/trips/${encodeURIComponent(tripId)}/catalog/destinations/${encodeURIComponent(destId)}`,
      {
        credentials: 'include',
        headers: guestToken ? { 'X-JG-Guest-Token': guestToken } : undefined,
      },
    );
    const ct = res.headers.get('content-type') || '';
    if (!res.ok || !ct.includes('json')) return null;
    const body = (await res.json()) as {
      photos?: Array<string | { url?: string; src?: string }>;
    };
    const list = body?.photos ?? [];
    const raw = list[photoIdx];
    if (!raw) return null;
    const url = typeof raw === 'string' ? raw : raw.url || raw.src;
    return url ? (resolvePhoto(url)?.src ?? url) : null;
  } catch {
    return null;
  }
}

// Resolve a POOL of hero photos for a trip-card slideshow (mirrors mobile's
// useMarqueePhotos, which rotates a multi-destination pool). Curated display
// photos win; otherwise fan out over the trip's stops. Empty pool → caller
// renders the accent gradient.
export async function resolveTripPhotos(trip: TripListItem): Promise<TripHero[]> {
  const d = (trip.display ?? {}) as DisplayCfg;

  // 1. Curated pool — every curated photo, in mobile's pin order.
  const curated: TripHero[] = [];
  const mh = bareUrl(d.marquee?.heroUrl);
  if (mh) curated.push({ url: mh });
  if (d.heroPrimaryDest) {
    const u = await fetchDestPhoto(trip.tripId, d.heroPrimaryDest.destId, d.heroPrimaryDest.photoIdx);
    if (u) curated.push({ url: u });
  }
  for (const s of d.heroSlides ?? []) {
    const u = await fetchDestPhoto(trip.tripId, s.destId, s.photoIdx);
    if (u) curated.push({ url: u });
  }
  for (const e of d.heroExtraUrls ?? []) {
    const u = bareUrl(e);
    if (u) curated.push({ url: u });
  }
  if (curated.length) return dedupeHeros(curated).slice(0, 12);

  // 2. Hero-API pool — fan out over stops (deduped, in trip order), gathering a
  //    few photos each until the pool is healthy. Skips Europeana B&W archival.
  const pool: TripHero[] = [];
  const seen = new Set<string>();
  for (const stay of trip.stays ?? []) {
    if (pool.length >= 6) break;
    if (!stay.destId || seen.has(stay.destId)) continue;
    seen.add(stay.destId);
    const q = await heroQueryForStay(stay);
    if (!q) continue;
    const r = await getHeroPhotos({ q, type: 'destination', n: 4 }).catch(() => null);
    for (const p of r?.photos ?? []) {
      // Europeana B&W skip is centralized in the /api/photos/hero endpoint now.
      const h = toHero(p);
      if (h) pool.push(h);
    }
  }
  return dedupeHeros(pool).slice(0, 8);
}
